import { WebDashboardContentSpecBlocksInner } from "@coeff/agent-types";
import { useCallback, useEffect, useRef, useState } from "react";

import { resolveBlockTitle } from "./BlockAutocompleteDropdown";

const OVERLAY_ID = "__block-picker-overlay__";
const SHIMMER_STYLE_ID = "__block-picker-shimmer-style__";
const HIGHLIGHT_ATTR = "data-block-picker-highlight";

const SHIMMER_CSS = `
@keyframes block-picker-pulse {
  0%, 100% { opacity: 0.05; }
  50% { opacity: 0.12; }
}
[${HIGHLIGHT_ATTR}]::before {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 1;
  border-radius: inherit;
  pointer-events: none;
  background: var(--color-primary, #3b82f6);
  animation: block-picker-pulse 2.4s ease-in-out infinite;
}
`;

interface UseBlockPickerOptions {
  targetDocument?: Document;
  blocks: WebDashboardContentSpecBlocksInner[];
  onBlockPicked: (blockId: string, blockTitle: string, blockType: string) => void;
}

export const useBlockPicker = ({
  targetDocument,
  blocks,
  onBlockPicked,
}: UseBlockPickerOptions) => {
  const [isPickerActive, setIsPickerActive] = useState(false);
  const cleanupRef = useRef<(() => void) | null>(null);
  const blocksRef = useRef(blocks);
  blocksRef.current = blocks;
  const onBlockPickedRef = useRef(onBlockPicked);
  onBlockPickedRef.current = onBlockPicked;

  const deactivatePicker = useCallback(() => {
    cleanupRef.current?.();
    cleanupRef.current = null;
    setIsPickerActive(false);
  }, []);

  const activatePicker = useCallback(() => {
    if (!targetDocument) {
      return;
    }
    deactivatePicker();

    setIsPickerActive(true);

    const doc = targetDocument;

    // 1. Inject transparent overlay scoped to mainContent (catches clicks outside blocks to dismiss)
    const mainContent = doc.getElementById("mainContent");
    const overlayParent = mainContent || doc.body;
    const overlay = doc.createElement("div");
    overlay.id = OVERLAY_ID;
    let prevMainContentPosition: string | null = null;
    if (mainContent) {
      prevMainContentPosition = mainContent.style.position;
      if (!prevMainContentPosition || prevMainContentPosition === "static") {
        mainContent.style.position = "relative";
      }
      Object.assign(overlay.style, {
        position: "absolute",
        inset: "0",
        zIndex: "9999",
        backgroundColor: "rgba(0, 0, 0, 0.15)",
        cursor: "crosshair",
        transition: "opacity 150ms",
      } satisfies Partial<CSSStyleDeclaration>);
    } else {
      Object.assign(overlay.style, {
        position: "fixed",
        inset: "0",
        zIndex: "9999",
        backgroundColor: "rgba(0, 0, 0, 0.15)",
        cursor: "crosshair",
        transition: "opacity 150ms",
      } satisfies Partial<CSSStyleDeclaration>);
    }
    overlayParent.appendChild(overlay);

    // 2. Inject shimmer animation stylesheet
    const styleEl = doc.createElement("style");
    styleEl.id = SHIMMER_STYLE_ID;
    styleEl.textContent = SHIMMER_CSS;
    doc.head.appendChild(styleEl);

    // 3. Find all block elements and elevate them above overlay
    const blockElements = Array.from(doc.querySelectorAll<HTMLElement>("[data-block-id]"));
    const originalStyles = new Map<
      HTMLElement,
      {
        zIndex: string;
        cursor: string;
        position: string;
        outline: string;
        outlineOffset: string;
        overflow: string;
      }
    >();

    for (const el of blockElements) {
      originalStyles.set(el, {
        zIndex: el.style.zIndex,
        cursor: el.style.cursor,
        position: el.style.position,
        outline: el.style.outline,
        outlineOffset: el.style.outlineOffset,
        overflow: el.style.overflow,
      });
      el.style.position = "relative";
      el.style.zIndex = "10000";
      el.style.cursor = "pointer";
      el.style.overflow = "hidden";
    }

    // 4. Hover / click handlers — outline + shimmer ::before via data attribute
    const handleMouseOver = (e: Event) => {
      const target = (e.target as HTMLElement).closest<HTMLElement>("[data-block-id]");
      if (target) {
        target.style.outline = "2px solid #3b82f6";
        target.style.outlineOffset = "2px";
        target.setAttribute(HIGHLIGHT_ATTR, "true");
      }
    };

    const handleMouseOut = (e: Event) => {
      const target = (e.target as HTMLElement).closest<HTMLElement>("[data-block-id]");
      if (target && target.getAttribute(HIGHLIGHT_ATTR)) {
        target.style.outline = "";
        target.style.outlineOffset = "";
        target.removeAttribute(HIGHLIGHT_ATTR);
      }
    };

    const handleClick = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      const target = (e.target as HTMLElement).closest<HTMLElement>("[data-block-id]");
      if (!target) {
        return;
      }

      const blockId = target.getAttribute("data-block-id");
      if (!blockId) {
        return;
      }

      const block = blocksRef.current.find(b => b.block_id === blockId);
      const title = block ? resolveBlockTitle(block) : blockId;
      const type = block?.type ?? "unknown";

      onBlockPickedRef.current(blockId, title, type);
      deactivatePicker();
    };

    const handleOverlayClick = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      deactivatePicker();
    };

    const handleKeyDown = (e: Event) => {
      if ((e as KeyboardEvent).key === "Escape") {
        e.preventDefault();
        deactivatePicker();
      }
    };

    for (const el of blockElements) {
      el.addEventListener("mouseover", handleMouseOver, true);
      el.addEventListener("mouseout", handleMouseOut, true);
      el.addEventListener("click", handleClick, true);
    }
    overlay.addEventListener("click", handleOverlayClick);
    doc.addEventListener("keydown", handleKeyDown, true);

    // 5. Cleanup function
    cleanupRef.current = () => {
      for (const el of blockElements) {
        const orig = originalStyles.get(el);
        if (orig) {
          el.style.zIndex = orig.zIndex;
          el.style.cursor = orig.cursor;
          el.style.position = orig.position;
          el.style.outline = orig.outline;
          el.style.outlineOffset = orig.outlineOffset;
          el.style.overflow = orig.overflow;
        }
        el.removeAttribute(HIGHLIGHT_ATTR);
        el.removeEventListener("mouseover", handleMouseOver, true);
        el.removeEventListener("mouseout", handleMouseOut, true);
        el.removeEventListener("click", handleClick, true);
      }
      overlay.removeEventListener("click", handleOverlayClick);
      doc.removeEventListener("keydown", handleKeyDown, true);
      overlay.remove();
      styleEl.remove();
      if (mainContent && prevMainContentPosition !== null) {
        mainContent.style.position = prevMainContentPosition;
      }
    };
  }, [targetDocument, deactivatePicker]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupRef.current?.();
    };
  }, []);

  return { isPickerActive, activatePicker, deactivatePicker };
};
